/**
 * Fleet side panel — the design's `.detail-panel` (admin.html #t-fleet):
 * row click keeps the fleet table visible and opens this compact
 * per-car pane on the right.
 *
 *   head      car number (enrollment id) · driver / vehicle · expand · close
 *   preview   camera snapshot slot (click = SnapshotDialog) + mode badge
 *   actions   Record/Stop (queues while the phone is offline)
 *   sections  collapsible Telemetry + Session history
 *
 * No eject here — that's the Roster reject action. The full
 * DeviceDetail page (settings, bitrate, commands log) stays one click
 * away via the expand button.
 */

import React, { useEffect, useState } from 'react';
import {
  Camera,
  Check,
  ChevronDown,
  Circle as RecordCircle,
  Maximize2,
  Pencil,
  Play,
  Share2,
  Square,
  Trash2,
  Video,
  X,
} from 'lucide-react';

import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from '@/components/ui/tooltip';
import { Spinner } from '@/components/ui/spinner';
import { cn, displayVehicle, formatBytes, formatRelative, parseUtcMs } from '@/lib/utils';
import api from '../services/api';
import { DeviceSession, FleetDevice } from '../types';
import { EnrollmentRow } from '../services/fleet';
import { LagInfo, SEVERITY_TEXT } from './viewer/lag';
import DeleteSessionDialog from './DeleteSessionDialog';
import ShareModal from './ShareModal';
import SnapshotDialog from './SnapshotDialog';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';

const Section: React.FC<{
  title: string;
  meta?: React.ReactNode;
  children: React.ReactNode;
}> = ({ title, meta, children }) => {
  const [open, setOpen] = useState(true);
  return (
    <div className="border-t border-border">
      <button
        type="button"
        onClick={() => setOpen((o) => !o)}
        className="flex w-full items-center gap-1.5 px-3 py-2 text-[10px] font-bold uppercase tracking-wider text-fg-secondary hover:text-fg"
      >
        <ChevronDown className={cn('size-3.5 transition-transform', !open && '-rotate-90')} />
        {title}
        <span className="ml-auto font-mono font-medium normal-case tracking-normal text-fg-muted">
          {meta}
        </span>
      </button>
      {open && <div className="px-3 pb-3">{children}</div>}
    </div>
  );
};

const Row: React.FC<{ label: string; value: React.ReactNode; tone?: string }> = ({
  label,
  value,
  tone,
}) => (
  <div className="flex items-baseline justify-between gap-2 border-b border-border/40 py-1.5 text-xs last:border-b-0">
    <span className="shrink-0 text-fg-muted">{label}</span>
    <span className={cn('min-w-0 truncate text-right font-mono', tone ?? 'text-fg')}>{value}</span>
  </div>
);

/** 'HH:MM:SS' local wall-clock from a naive-UTC backend timestamp. */
const fmtClock = (iso: string | null): string => {
  if (!iso) return '—';
  const ms = parseUtcMs(iso);
  return Number.isFinite(ms) ? new Date(ms).toLocaleTimeString([], { hour12: false }) : '—';
};

interface FleetDevicePanelProps {
  device: FleetDevice;
  enrollment: EnrollmentRow | null;
  /** Stream-health readout from the fleet table's scale (null = not recording). */
  lag: LagInfo | null;
  busy: boolean;
  /** Current event id — session history is scoped to it by default
   *  (rental/handover privacy: a past renter's recordings must not
   *  surface for the new event's operator). Null = no event selected
   *  (the ?all=1 ops view) → unscoped. */
  eventId: number | null;
  onClose: () => void;
  /** Open the full DeviceDetail page (settings, commands, telemetry). */
  onExpand: () => void;
  onCommand: (kind: string, payload?: Record<string, any>) => Promise<void>;
}

export const FleetDevicePanel: React.FC<FleetDevicePanelProps> = ({
  device: d,
  enrollment,
  lag,
  busy,
  eventId,
  onClose,
  onExpand,
  onCommand,
}) => {
  const [sessions, setSessions] = useState<DeviceSession[]>([]);
  const [previewCam, setPreviewCam] = useState<string | null>(null);
  // Rename: inline edit of the device display name (d.name). Optimistic
  // local overlay so the head updates instantly; the parent's poll
  // reconciles other clients within 10 s.
  const [editingName, setEditingName] = useState(false);
  const [nameDraft, setNameDraft] = useState('');
  const [nameOverride, setNameOverride] = useState<string | null | undefined>(undefined);
  // Session pending deletion (drives the confirm dialog).
  const [deleteTarget, setDeleteTarget] = useState<DeviceSession | null>(null);
  // Explicit admin escape from the event scope (resets per device).
  const [allHistory, setAllHistory] = useState(false);
  // Per-row ⋯ menu + its async states, share
  // dialog, camera-selector busy flag.
  const [menuFor, setMenuFor] = useState<string | null>(null);
  const [rowBusy, setRowBusy] = useState<Record<string, string>>({});
  const [shareOpen, setShareOpen] = useState(false);
  const [cameraBusy, setCameraBusy] = useState(false);
  useEffect(() => {
    setAllHistory(false);
    setNameOverride(undefined);
    setEditingName(false);
    setMenuFor(null);
    setRowBusy({});
    setShareOpen(false);
  }, [d.device_id]);
  const scopedEventId = allHistory ? null : eventId;
  // Displayed device name: optimistic override wins, else the prop.
  const deviceName = nameOverride !== undefined ? nameOverride : d.name;

  const refreshSessions = React.useCallback(async () => {
    try {
      const ses = await api.listDeviceSessions(d.device_id, scopedEventId);
      setSessions(ses);
    } catch {
      // endpoint may not exist on older backends
    }
  }, [d.device_id, scopedEventId]);

  const submitRename = async () => {
    const next = nameDraft.trim();
    setEditingName(false);
    if (next === (deviceName ?? '')) return;
    setNameOverride(next || null); // optimistic
    try {
      const updated = await api.renameDevice(d.device_id, next || null);
      setNameOverride(updated.name ?? null);
    } catch {
      setNameOverride(undefined); // revert to prop on failure
    }
  };

  // Same cadence as DeviceDetail's session list (10 s refresh).
  useEffect(() => {
    let cancelled = false;
    const refresh = async () => {
      try {
        const ses = await api.listDeviceSessions(d.device_id, scopedEventId);
        if (!cancelled) setSessions(ses);
      } catch {
        // endpoint may not exist on older backends
      }
    };
    void refresh();
    const id = window.setInterval(refresh, 10_000);
    return () => {
      cancelled = true;
      window.clearInterval(id);
    };
  }, [d.device_id, scopedEventId]);

  const recording = d.mode != null && d.mode !== 'idle';
  const firstCam = d.cameras[0];
  const signal = [
    d.network_quality?.effective_type?.toUpperCase() ?? null,
    d.network_quality?.downlink_mbps != null
      ? `${d.network_quality.downlink_mbps.toFixed(1)} Mb/s`
      : null,
  ]
    .filter(Boolean)
    .join(' · ');

  return (
    <div className="flex flex-col">
      {/* Head (mockup .dp-head) */}
      <div className="flex items-center gap-2.5 px-3 py-2.5">
        <div className="font-mono text-[22px] font-bold leading-none text-accent">
          {enrollment ? enrollment.id : '—'}
        </div>
        <div className="min-w-0 flex-1">
          {editingName ? (
            <div className="flex items-center gap-1">
              <Input
                autoFocus
                value={nameDraft}
                onChange={(e) => setNameDraft(e.target.value)}
                onKeyDown={(e) => {
                  if (e.key === 'Enter') void submitRename();
                  if (e.key === 'Escape') setEditingName(false);
                }}
                placeholder={d.hostname || d.device_id}
                maxLength={255}
                className="h-7 text-sm"
              />
              <Button
                variant="ghost"
                size="icon"
                className="size-7 shrink-0 text-status-online"
                onClick={() => void submitRename()}
                aria-label="Save name"
              >
                <Check className="size-4" />
              </Button>
            </div>
          ) : (
            <div className="flex items-center gap-1">
              <span className="truncate text-sm font-bold text-fg">
                {enrollment?.driver_name || deviceName || d.device_id}
              </span>
              <Button
                variant="ghost"
                size="icon"
                className="size-6 shrink-0 text-fg-muted hover:text-fg"
                onClick={() => {
                  setNameDraft(deviceName ?? '');
                  setEditingName(true);
                }}
                aria-label="Rename device"
                title="Rename device"
              >
                <Pencil className="size-3" />
              </Button>
            </div>
          )}
          <div className="truncate text-[10px] text-fg-muted">
            {displayVehicle(enrollment?.vehicle) ? `${displayVehicle(enrollment?.vehicle)} · ` : ''}
            {d.device_id}
          </div>
        </div>
        {/* Command in flight — the action buttons only disable, which
            read as dead buttons; one spinner tells the truth. */}
        {busy && <Spinner size={14} />}
        {/* Labeled, not a bare expand glyph — this is the panel's main
            escape hatch to settings/commands/telemetry. */}
        <TooltipProvider>
          <Tooltip>
            <TooltipTrigger asChild>
              <Button
                variant="outline"
                size="sm"
                onClick={onExpand}
                aria-label="Open full detail"
                // hover:bg override matters: the outline variant's default
                // hover is bg-accent — amber fill + amber text = invisible.
                className="shrink-0 gap-1.5 whitespace-nowrap border-border text-xs text-fg-secondary hover:border-accent hover:bg-accent/10 hover:text-accent"
              >
                <Maximize2 className="size-3.5" />
                Full details
              </Button>
            </TooltipTrigger>
            <TooltipContent>Settings, commands, sessions & telemetry</TooltipContent>
          </Tooltip>
        </TooltipProvider>
        <Button variant="ghost" size="icon" onClick={onClose} aria-label="Close panel">
          <X className="size-4" />
        </Button>
      </div>

      {/* Preview (mockup .dp-preview) — snapshot on click */}
      <button
        type="button"
        disabled={!firstCam}
        onClick={() =>
          firstCam && setPreviewCam(firstCam.name || firstCam.camera_id)
        }
        title={firstCam ? 'Camera snapshot' : 'No cameras reported'}
        className="relative flex aspect-video w-full items-center justify-center bg-bg-video"
      >
        <Video className="size-9 text-white/20" />
        <span
          className={cn(
            'absolute left-2 top-2 rounded bg-black/70 px-1.5 py-0.5 font-mono text-[10px] font-bold',
            recording ? 'text-status-error' : 'text-fg-muted',
          )}
        >
          {recording ? `● ${d.mode}` : d.online ? 'idle' : 'offline'}
        </span>
        {lag && (
          <span
            className={cn(
              'absolute bottom-2 right-2 rounded bg-black/70 px-1.5 py-0.5 font-mono text-[10px]',
              SEVERITY_TEXT[lag.severity],
            )}
          >
            {lag.label}
          </span>
        )}
      </button>
      {d.cameras.length > 1 && (
        <div className="flex flex-wrap gap-1 px-3 pt-2">
          {d.cameras.map((c) => (
            <button
              key={c.camera_id}
              type="button"
              onClick={() => setPreviewCam(c.name || c.camera_id)}
              className={cn(
                'inline-flex items-center gap-1 rounded-full border bg-bg-elevated px-2 py-0.5 font-mono text-[10px]',
                c.is_hdmi && c.signal_locked === false
                  ? 'border-status-warning text-status-warning'
                  : 'border-border text-fg-secondary hover:text-fg',
              )}
            >
              <Camera className="size-3" />
              {c.name || c.camera_id}
            </button>
          ))}
        </div>
      )}

      {/* Actions (mockup .dp-actions) — repo verbs */}
      <div className="flex gap-1.5 p-3">
        <TooltipProvider>
          {/* Queue-while-offline is deliberate. */}
          <Tooltip>
            <TooltipTrigger asChild>
              <span className="flex-1">
                <Button
                  size="sm"
                  variant="outline"
                  disabled={busy || d.mode === 'buffered'}
                  onClick={() => onCommand('phone.start_recording', {})}
                  className="w-full border-accent-purple text-accent-purple hover:bg-accent-purple/10"
                >
                  <RecordCircle />
                  Record
                </Button>
              </span>
            </TooltipTrigger>
            <TooltipContent>
              {d.online
                ? 'Tell the phone to start recording'
                : 'Phone offline — command queues and runs on reconnect'}
            </TooltipContent>
          </Tooltip>
          <Tooltip>
            <TooltipTrigger asChild>
              <span className="flex-1">
                <Button
                  size="sm"
                  variant="outline"
                  disabled={busy || d.mode === 'idle' || d.mode == null}
                  onClick={() => onCommand('phone.stop_recording', {})}
                  className="w-full border-status-error text-status-error hover:bg-status-error/10"
                >
                  <Square />
                  Stop
                </Button>
              </span>
            </TooltipTrigger>
            <TooltipContent>
              {d.online
                ? 'Stop phone recording'
                : 'Phone offline — stop queues and runs on reconnect'}
            </TooltipContent>
          </Tooltip>
        </TooltipProvider>
      </div>

      {/* The daily-driver controls that used to
          force a jump to the full DeviceDetail page: Share (current
          session) + camera selector. */}
      <div className="flex items-center gap-1.5 border-t border-border/40 px-3 py-2">
        <Select
          value={
            d.camera_selector === 'front' || d.camera_selector === 'external'
              ? d.camera_selector
              : 'back'
          }
          disabled={cameraBusy}
          onValueChange={async (value) => {
            setCameraBusy(true);
            try {
              await api.setDeviceCameraSelector(
                d.device_id,
                value === 'back' ? null : (value as 'front' | 'external'),
              );
            } catch {
              /* parent poll reconciles */
            } finally {
              setCameraBusy(false);
            }
          }}
        >
          <SelectTrigger
            className="h-8 flex-1 text-xs"
            title="Which camera the phone records from (applies next session)"
          >
            <SelectValue />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="back">Phone back (default)</SelectItem>
            <SelectItem value="front">Phone front</SelectItem>
            <SelectItem value="external">External USB camera</SelectItem>
          </SelectContent>
        </Select>
        <Button
          size="sm"
          variant="outline"
          disabled={!d.current_session_id}
          title={
            d.current_session_id
              ? 'Share the live session'
              : 'No live session to share'
          }
          onClick={() => setShareOpen(true)}
          className="shrink-0 gap-1 border-border text-xs text-fg-secondary hover:border-accent hover:bg-accent/10 hover:text-accent"
        >
          <Share2 className="size-3.5" />
          Share
        </Button>
      </div>

      {/* Telemetry (mockup .dp-section) */}
      <Section title="Telemetry">
        <Row
          label="Status"
          // Offline devices keep their LAST KNOWN arm state — an armed
          // phone that drops offline reads "offline · armed (stale)",
          // never a bare "offline" that hides the arming.
          value={`${d.status}${
            enrollment
              ? ` · ${enrollment.state}${!d.online && enrollment.state === 'armed' ? ' (stale)' : ''}`
              : ''
          }`}
          tone={
            d.online
              ? 'text-status-online'
              : enrollment?.state === 'armed'
                ? 'text-status-warning'
                : 'text-fg-muted'
          }
        />
        {d.battery?.level != null && (
          <Row
            label="Battery"
            value={`${Math.round(d.battery.level * 100)}%${d.battery.charging ? ' ⚡' : ''}`}
            tone={
              d.battery.level >= 0.5
                ? 'text-status-online'
                : d.battery.level >= 0.2
                  ? 'text-status-warning'
                  : 'text-status-error'
            }
          />
        )}
        <Row
          label="Storage"
          // Disk numbers are sticky across disconnects — age the
          // value instead of dropping to "— free / —".
          value={
            d.disk_free_bytes != null || d.disk_total_bytes != null
              ? `${formatBytes(d.disk_free_bytes)} free / ${formatBytes(d.disk_total_bytes)}${
                  !d.online && d.disk_reported_at
                    ? ` · ${formatRelative(d.disk_reported_at)}`
                    : ''
                }`
              : 'no report yet'
          }
        />
        {signal && <Row label="Signal" value={signal} />}
        {lag && (
          <Row label="Lag" value={lag.label} tone={SEVERITY_TEXT[lag.severity]} />
        )}
        <Row
          label="Last seen"
          value={formatRelative(d.last_seen_at)}
          tone={d.online ? undefined : 'text-status-error'}
        />
        {d.upload_pending != null && d.upload_pending > 0 && (
          <Row label="Uploads pending" value={d.upload_pending} tone="text-status-warning" />
        )}
      </Section>

      {/* Session history (mockup .dp-section sess2 rows) — scoped to the
          current event by default; "all events" is the explicit admin
          escape (rental/handover privacy). */}
      <Section
        title="Session history"
        meta={`${sessions.length} sessions${scopedEventId != null ? ' · this event' : ''}`}
      >
        {eventId != null && (
          <div className="flex items-center justify-end pb-1">
            <button
              type="button"
              onClick={() => setAllHistory((v) => !v)}
              title={
                allHistory
                  ? 'Showing every session this device ever recorded — click to scope to the current event'
                  : 'Scoped to the current event — click to show all history (incl. other events)'
              }
              className={cn(
                'rounded-sm border px-1.5 py-px font-mono text-[9px] uppercase tracking-wide',
                allHistory
                  ? 'border-status-warning text-status-warning'
                  : 'border-border text-fg-muted hover:text-fg',
              )}
            >
              {allHistory ? 'showing all events' : 'this event only'}
            </button>
          </div>
        )}
        {sessions.length === 0 ? (
          <div className="py-2 text-xs text-fg-muted">
            {scopedEventId != null ? 'no sessions in this event yet' : 'no sessions yet'}
          </div>
        ) : (
          sessions.slice(0, 8).map((s) => {
            const live = s.session_id === d.current_session_id && recording;
            return (
              <div
                key={s.session_id}
                className="flex items-center gap-2 border-b border-border/40 py-1.5 text-xs last:border-b-0"
              >
                <span className="w-[52px] shrink-0 font-mono text-fg-secondary">
                  {fmtClock(s.started_at)}
                </span>
                <span className="min-w-0 flex-1 truncate font-mono text-[11px] text-fg-muted">
                  {s.session_id}
                </span>
                <Badge variant="outline" className="shrink-0 justify-center font-mono text-[10px]">
                  {s.segment_count} seg
                </Badge>
                {s.upload_paused && (
                  <span
                    title="Uploads paused for this session"
                    className="shrink-0 font-mono text-[10px] text-status-warning"
                  >
                    ⏸
                  </span>
                )}
                {live ? (
                  <span className="shrink-0 font-mono text-[10px] font-bold text-status-error">
                    ● live
                  </span>
                ) : (
                  <span className="w-8 shrink-0" />
                )}
                <Button
                  size="icon"
                  variant="ghost"
                  className="h-6 w-6 shrink-0 text-accent-purple hover:bg-accent-purple/10"
                  title="Play in synced viewer"
                  aria-label={`Play session ${s.session_id}`}
                  onClick={() => {
                    window.location.href = `/play/${encodeURIComponent(s.session_id)}`;
                  }}
                >
                  <Play className="size-3.5" />
                </Button>
                {/* Mirrors the DeviceDetail ⋯ actions menu,
                    compacted: Upload now, Pause/Resume, Delete. */}
                <div className="relative shrink-0">
                  <Button
                    size="icon"
                    variant="ghost"
                    className="h-6 w-6 text-fg-muted hover:text-fg"
                    aria-label={`Session actions for ${s.session_id}`}
                    aria-expanded={menuFor === s.session_id}
                    onClick={() =>
                      setMenuFor(menuFor === s.session_id ? null : s.session_id)
                    }
                  >
                    ⋯
                  </Button>
                  {menuFor === s.session_id && (
                    <>
                      <div className="fixed inset-0 z-40" onClick={() => setMenuFor(null)} />
                      <div className="absolute right-0 top-7 z-50 w-52 overflow-hidden rounded-md border border-border bg-bg-elevated py-1 shadow-modal">
                        <button
                          type="button"
                          disabled={rowBusy[s.session_id] === 'uploading'}
                          title="Allow this session to upload over a metered link now"
                          onClick={async () => {
                            setRowBusy((m) => ({ ...m, [s.session_id]: 'uploading' }));
                            try {
                              await api.uploadNow(s.session_id);
                              setRowBusy((m) => ({ ...m, [s.session_id]: 'upload-sent' }));
                            } catch {
                              setRowBusy((m) => ({ ...m, [s.session_id]: 'upload-error' }));
                            }
                          }}
                          className="flex w-full items-center gap-2 px-3 py-2 text-left font-mono text-xs text-fg hover:bg-bg-surface disabled:opacity-40"
                        >
                          {rowBusy[s.session_id] === 'uploading'
                            ? 'sending…'
                            : rowBusy[s.session_id] === 'upload-sent'
                              ? 'Upload now — sent ✓'
                              : rowBusy[s.session_id] === 'upload-error'
                                ? 'Upload now — failed'
                                : '📶 Upload now'}
                        </button>
                        <button
                          type="button"
                          disabled={rowBusy[s.session_id] === 'pause-sending'}
                          title="Pause or resume uploading of this session's segments (queues if the phone is offline)"
                          onClick={async () => {
                            const next = !s.upload_paused;
                            setRowBusy((m) => ({ ...m, [s.session_id]: 'pause-sending' }));
                            try {
                              await api.setSessionUploadPause(d.device_id, s.session_id, next);
                              setRowBusy((m) => {
                                const n = { ...m };
                                delete n[s.session_id];
                                return n;
                              });
                              await refreshSessions();
                            } catch {
                              setRowBusy((m) => ({ ...m, [s.session_id]: 'pause-error' }));
                            }
                          }}
                          className="flex w-full items-center gap-2 px-3 py-2 text-left font-mono text-xs text-status-warning hover:bg-bg-surface disabled:opacity-40"
                        >
                          {rowBusy[s.session_id] === 'pause-sending'
                            ? s.upload_paused
                              ? 'resuming…'
                              : 'pausing…'
                            : rowBusy[s.session_id] === 'pause-error'
                              ? 'pause — failed, retry'
                              : s.upload_paused
                                ? '▶ Resume uploads'
                                : '⏸ Pause uploads'}
                        </button>
                        <button
                          type="button"
                          disabled={s.session_id === d.current_session_id}
                          title={
                            s.session_id === d.current_session_id
                              ? 'Stop the recording before deleting'
                              : 'Delete session (removes footage permanently)'
                          }
                          onClick={() => {
                            setMenuFor(null);
                            setDeleteTarget(s);
                          }}
                          className="flex w-full items-center gap-2 px-3 py-2 text-left font-mono text-xs text-fg-muted hover:bg-status-error/10 hover:text-status-error disabled:opacity-30"
                        >
                          <Trash2 className="size-3.5" />
                          Delete session…
                        </button>
                      </div>
                    </>
                  )}
                </div>
              </div>
            );
          })
        )}
      </Section>

      {d.current_session_id && (
        <ShareModal
          open={shareOpen}
          onClose={() => setShareOpen(false)}
          deviceId={d.device_id}
          sessionId={d.current_session_id}
          label={`${deviceName || d.device_id} · ${d.current_session_id}`}
        />
      )}
      <DeleteSessionDialog
        sessionId={deleteTarget?.session_id ?? null}
        segmentCount={deleteTarget?.segment_count}
        onClose={() => setDeleteTarget(null)}
        onConfirm={async (sid) => {
          await api.deleteSession(sid);
          await refreshSessions();
        }}
      />

      {previewCam && (
        <SnapshotDialog
          open={!!previewCam}
          onClose={() => setPreviewCam(null)}
          deviceId={d.device_id}
          cameraId={previewCam}
          cameraLabel={previewCam}
        />
      )}
    </div>
  );
};

export default FleetDevicePanel;
